import { useSelector } from 'react-redux';
import { getQueryBuilderSteps, getSelectedLayerId } from '@/store-selectors';
import { foldPredicates } from '@/util/fp';
import { useAppSelector } from '@/store/hooks';
import { selectLastUpdateMethod } from '@/features/query-builder-steps/queryBuilderStepsSlice';
import Layer from '@/components/layers/Layer';
import { LAST_UPDATE_METHOD } from '@/features/query-builder-steps/constants';

type Step = Layer;

const hashSteps = (steps: ReturnType<typeof getQueryBuilderSteps>) => {
  const str = JSON.stringify(steps);
  try {
    return btoa(str);
  } catch (e) {
    return str;
  }
};
const isNotTemplate = (s: Step) => !s.isTemplate();
const isEnabled = (s: Step) => s._enabled;

const shouldProduceNewQuery = foldPredicates.withAnd(isNotTemplate, isEnabled);

/**
 * We use this mutable variable instead of React.useRef, since React.useRef keeps the value
 * between re-renders BUT after a component is UNMOUNTED the value is lost but we need to keep track of it.
 */
let previousStepsHash = '';
export const useGetUpdatedSteps = () => {
  const steps = useSelector(getQueryBuilderSteps);
  const lastUpdateMethod = useAppSelector(selectLastUpdateMethod);

  const selectedStepId = useSelector(getSelectedLayerId);

  const lastSelectedStepIndex = steps.findIndex(
    (s) => s._id === selectedStepId,
  );
  const newStepsHash = hashSteps(
    steps.slice(0, lastSelectedStepIndex + 1).filter(shouldProduceNewQuery),
  );

  if (lastUpdateMethod !== LAST_UPDATE_METHOD.stepsSidebarInteraction) {
    previousStepsHash = newStepsHash;
    return [[], [], undefined] as const;
  }

  const stepsHaveChanged = steps.length && newStepsHash !== previousStepsHash;

  if (stepsHaveChanged) {
    previousStepsHash = newStepsHash;
    const activeSteps = steps.slice(0, lastSelectedStepIndex + 1);
    const inactiveSteps = steps.slice(lastSelectedStepIndex + 1);

    return [activeSteps, inactiveSteps, newStepsHash] as const;
  }

  return [[], [], undefined] as const;
};
